import { useState } from "react"

import { PlusIcon } from "@heroicons/react/20/solid"

import type { BatteryProposalAiComputeDto } from "~/api/dto/battery-proposal-compute.dto"
import useBatteryComputePreview from "~/api/hooks/useBatteryComputePreview"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  COMPUTE_SAVED_ON_PROPOSAL,
  COMPUTE_TERMS_NOTE,
  ComputeCardGrid,
  type ComputeDraft,
} from "../../ProposalSetup/compute/ComputeCatalog"

export const BATTERY_COMPUTE_EMPTY_NOTICE = `AI compute isn't offered for this proposal's location, utility or Finance Plan.`
export const BATTERY_COMPUTE_PLAN_CHANGED_NOTICE = `AI compute nodes are offered on the saved Finance Plan and battery. Save the battery first, then add a node.`
export const BATTERY_COMPUTE_LOCKED_EMPTY_NOTICE = `No AI compute node on this proposal.`
const TEST_ID_PREFIX = `battery-compute`

const sectionLabelClass = `font-display text-[length:var(--step-0)] font-semibold text-bp-text`
const fieldLabelClass = `text-[length:var(--step--2)] font-semibold tracking-[0.14em] text-bp-text-muted uppercase`
const noteClass = `text-[length:var(--step--2)] text-bp-text-muted`

/**
 * Optional equipment under the battery: the one Vendor AI compute node, as
 * V1 Solar offers it beside Smart Panel and EV Charger. PowurOS offers nodes
 * on the saved Finance Plan and battery, so the cards follow what is saved;
 * the draft lives with the form, whose one Save writes it.
 */
export const BatteryOptionalEquipment = ({
  proposalId,
  saved,
  draft,
  hasSavedEquipment,
  planChanged,
  locked,
  disabled,
  onChange,
}: {
  proposalId: string
  saved: BatteryProposalAiComputeDto | null
  draft: ComputeDraft
  /** No node is offered before the Finance Plan and battery are saved. */
  hasSavedEquipment: boolean
  /** The drafted Finance Plan or battery differs from the saved one. */
  planChanged: boolean
  /** Financing locks the selection. */
  locked: boolean
  /** A save is in flight. */
  disabled: boolean
  onChange: () => void
}): JSX.Element | null => {
  const [expanded, setExpanded] = useState(false)
  const offeringReady = hasSavedEquipment && !planChanged && !locked
  const preview = useBatteryComputePreview(proposalId, {
    enabled: offeringReady,
  })
  const offered = preview.data?.options ?? []

  // As V1: listed only when PowurOS offers a node, or one is already saved.
  // Once open it stays, so a node withdrawn on save leaves the empty notice.
  if (
    !hasSavedEquipment ||
    (!expanded && saved == null && offered.length === 0 && !preview.isError)
  ) {
    return null
  }

  if (!expanded) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1.5 text-[length:var(--step--1)] font-semibold text-bp-accent hover:text-bp-accent-hover"
        >
          <PlusIcon aria-hidden="true" className="size-4" />
          Add optional equipment (AI Compute)
        </button>
        {draft.selectedId != null ? (
          <span
            data-testid="battery-compute-added"
            className="rounded-full border border-bp-border-strong bg-bp-elevated px-3 py-1 text-[length:var(--step--2)] font-semibold text-bp-text"
          >
            AI Compute Added
          </span>
        ) : null}
      </div>
    )
  }

  // What can't use the saved Finance Plan's offering still shows the saved
  // node, so it can be removed: removing needs nothing from PowurOS.
  const savedOnly = (hint?: string): JSX.Element => (
    <ComputeCardGrid
      options={[]}
      saved={saved}
      savedHint={hint}
      draft={draft}
      disabled={disabled || locked}
      onChange={onChange}
      emptyNotice={BATTERY_COMPUTE_LOCKED_EMPTY_NOTICE}
      testIdPrefix={TEST_ID_PREFIX}
    />
  )

  return (
    <section
      aria-label="Optional equipment"
      className="flex flex-col gap-3"
      data-testid="battery-optional-equipment"
    >
      <div className="flex items-center justify-between">
        <h3 className={sectionLabelClass}>Optional equipment</h3>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-[length:var(--step--2)] font-medium text-bp-text-muted hover:text-bp-text"
        >
          Hide
        </button>
      </div>
      <span className={fieldLabelClass}>AI Compute</span>
      {locked ? (
        savedOnly(COMPUTE_SAVED_ON_PROPOSAL)
      ) : planChanged ? (
        <>
          <p role="status" className={noteClass}>
            {BATTERY_COMPUTE_PLAN_CHANGED_NOTICE}
          </p>
          {saved ? savedOnly(COMPUTE_SAVED_ON_PROPOSAL) : null}
        </>
      ) : preview.isLoading ? (
        <p role="status" className={noteClass}>
          Loading AI compute nodes from PowurOS
        </p>
      ) : preview.isError ? (
        <>
          <div className="flex flex-col gap-2">
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {parseAxiosError(
                preview.error,
                `Couldn't load AI compute nodes from PowurOS. Try again.`,
              )}
            </p>
            <button
              type="button"
              onClick={() => {
                void preview.refetch()
              }}
              className="self-start rounded-md border border-bp-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-bp-text-soft transition-colors hover:border-bp-accent hover:text-bp-text"
            >
              Retry
            </button>
          </div>
          {saved ? savedOnly(COMPUTE_SAVED_ON_PROPOSAL) : null}
        </>
      ) : (
        <ComputeCardGrid
          options={offered}
          saved={saved}
          draft={draft}
          disabled={disabled}
          onChange={onChange}
          emptyNotice={BATTERY_COMPUTE_EMPTY_NOTICE}
          testIdPrefix={TEST_ID_PREFIX}
        />
      )}
      <p className={noteClass}>{COMPUTE_TERMS_NOTE}</p>
    </section>
  )
}
